import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  useEditJobCardMutation,
  useEditIMSSmartnodeMutation,
  useDownloadDigitalCOCMutation,
  useAddChangeLogMutation,
} from "../api/apiSlice";
import { useSelector, useDispatch } from "react-redux";
import { getUserName } from "../Authentication/authSlice";
import { BG, CARD_BG, BORDER, AMBER } from "../../Global/colors";

const dialogSx = {
  "& .MuiDialog-paper": {
    backgroundColor: "#161B22",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "14px",
    color: "#fff",
  },
  "& .MuiDialogTitle-root": { color: "#fff", fontWeight: 700 },
};

export function DigitalCOCCapture() {
  const { id, job_card_reference_id, serial_number, imsi_number, install_state } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user_name = useSelector(getUserName);

  const existing = location.state ?? {};
  const existingFront = existing.existingFront ?? null;
  const existingBack = existing.existingBack ?? null;
  const existingSubDb = existing.existingSubDb ?? null;
  const existingPdf = existing.existingPdf ?? null;
  const vetStates = existing.vetStates ?? {};

  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [mode, setMode] = useState(existingPdf && !existingFront ? "pdf" : "photos");
  const [frontPhoto, setFrontPhoto] = useState(null);
  const [backPhoto, setBackPhoto] = useState(null);
  const [subDbPhoto, setSubDbPhoto] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  const [editJobCard, { isSuccess: isEditJobCardSuccess, isLoading: isEditJobCardLoading }] = useEditJobCardMutation();
  const [editIMSSmartNode, { isSuccess: isEditIMSSmartNodeSuccess }] = useEditIMSSmartnodeMutation();
  const [addChangeLog, { isSuccess: addChangeLogSuccess }] = useAddChangeLogMutation();

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleSubmit = () => {
    const body = new FormData();
    if (mode === "photos") {
      if (frontPhoto) body.append("digital_coc_image", frontPhoto);
      if (backPhoto) body.append("digital_coc_image2", backPhoto);
      if (subDbPhoto) body.append("digital_coc_image3", subDbPhoto);
    } else {
      if (pdfFile) body.append("digital_coc_pdf", pdfFile);
    }
    body.append("reference_id", job_card_reference_id);
    body.append("qe_job_state", "Approved");
    body.append("qe_name", user_name);
    body.append("state", "COM");
    body.append("photo_vet_states", JSON.stringify(vetStates));
    editJobCard({ body });
  };

  React.useEffect(() => {
    if (isEditJobCardSuccess && install_state !== "UN") {
      editIMSSmartNode({
        body: {
          serial_number,
          imsi_number,
          status: "In Field",
          change: "QE approved install and device moved to in field",
        },
      });
    } else if (isEditJobCardSuccess && install_state === "UN") {
      addChangeLog({ body: { serial_number, change: "QE Approved uninstall" } });
      editIMSSmartNode({
        body: {
          serial_number,
          imsi_number,
          status: "Approved",
          change: "QE Approved uninstall",
        },
      });
    }
  }, [isEditJobCardSuccess]);

  React.useEffect(() => {
    if (isEditIMSSmartNodeSuccess || addChangeLogSuccess) {
      setOpenApproveDialog(true);
    }
  }, [isEditIMSSmartNodeSuccess, addChangeLogSuccess]);

  if (!isMobile) {
    return (
      <>
        <Helmet><style>{`body { background: ${BG}; }`}</style></Helmet>
        <Box sx={{ pt: "100px", textAlign: "center" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
            Sorry, this feature is only available on mobile devices.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet><style>{`body { background: ${BG}; }`}</style></Helmet>
      <Box sx={{ px: "24px", pt: "80px", pb: "48px", maxWidth: "480px", mx: "auto" }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: "28px" }}>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            Capture digital C.O.C.
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.35)", mt: "8px", wordBreak: "break-all" }}>
            JC reference {job_card_reference_id}
          </Typography>
        </Box>

        {/* Mode tabs */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: "20px" }}>
          <Box
            sx={{
              display: "inline-flex",
              backgroundColor: CARD_BG,
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              p: "4px",
              gap: "4px",
            }}
          >
            {["photos", "pdf"].map((m) => (
              <Button
                key={m}
                onClick={() => setMode(m)}
                sx={{
                  borderRadius: "7px",
                  px: "20px",
                  py: "7px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  backgroundColor: mode === m ? AMBER : "transparent",
                  color: mode === m ? "#0d1117" : "rgba(255,255,255,0.6)",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: mode === m ? AMBER : "rgba(255,255,255,0.06)",
                    boxShadow: "none",
                  },
                  minWidth: "110px",
                }}
              >
                {m === "photos" ? "Photos" : "Upload PDF"}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Hint text */}
        <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", mb: "24px", lineHeight: 1.5 }}>
          Paper COC? Photograph the front and back. Digital-platform COC? Switch to PDF.
        </Typography>

        {mode === "photos" ? (
          <PhotosMode
            frontPhoto={frontPhoto}
            setFrontPhoto={setFrontPhoto}
            backPhoto={backPhoto}
            setBackPhoto={setBackPhoto}
            subDbPhoto={subDbPhoto}
            setSubDbPhoto={setSubDbPhoto}
            existingFront={existingFront}
            existingBack={existingBack}
            existingSubDb={existingSubDb}
            onSubmit={handleSubmit}
            isLoading={isEditJobCardLoading}
          />
        ) : (
          <PdfMode
            pdfFile={pdfFile}
            setPdfFile={setPdfFile}
            existingPdf={existingPdf}
            onSubmit={handleSubmit}
            isLoading={isEditJobCardLoading}
          />
        )}

        <ApproveDialog
          open={openApproveDialog}
          onClose={() => setOpenApproveDialog(false)}
          id={id}
          job_card_reference_id={job_card_reference_id}
        />
      </Box>
    </>
  );
}

function PhotoSlot({ label, required, photo, setPhoto, inputId, existingUrl, onRemoveCallback }) {
  const inputRef = useRef(null);
  const [localUrl, setLocalUrl] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);

  // When photo is cleared externally (e.g. Clear All), reset local preview
  useEffect(() => {
    if (!photo) setLocalUrl(null);
  }, [photo]);

  const handleCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (localUrl) URL.revokeObjectURL(localUrl);
    setPhoto(file);
    setLocalUrl(URL.createObjectURL(file));
    e.target.value = "";
  };

  const handleRetake = () => {
    setActionOpen(false);
    inputRef.current?.click();
  };

  const handleRemove = () => {
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl(null);
    setPhoto(null);
    setActionOpen(false);
    onRemoveCallback?.();
  };

  const previewUrl = localUrl ?? existingUrl ?? null;
  const hasImage = !!previewUrl;
  const subtitle = required
    ? "Retake replaces this photo. Removing forces you to capture it again before continuing."
    : "Retake replaces this photo, or remove it — it's optional.";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: required ? AMBER : "rgba(255,255,255,0.4)",
          textTransform: "uppercase",
          mb: "2px",
        }}
      >
        {required ? "Required" : "Optional"}
      </Typography>
      <input
        ref={inputRef}
        accept="image/*"
        id={inputId}
        type="file"
        capture="environment"
        onChange={handleCapture}
        style={{ display: "none" }}
      />
      <Box sx={{ position: "relative", width: "100%" }}>
        <Box
          onClick={hasImage ? () => setActionOpen(true) : () => inputRef.current?.click()}
          sx={{
            aspectRatio: "1",
            backgroundColor: CARD_BG,
            border: `1px dashed ${hasImage ? AMBER : "rgba(255,255,255,0.15)"}`,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          {hasImage ? (
            <img src={previewUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <CameraAltOutlinedIcon sx={{ color: AMBER, opacity: 0.55, fontSize: "1.8rem" }} />
          )}
        </Box>
        {hasImage && (
          <IconButton
            size="small"
            onClick={() => setActionOpen(true)}
            sx={{
              position: "absolute",
              top: "6px",
              right: "6px",
              width: "22px",
              height: "22px",
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              padding: 0,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <MoreHorizIcon sx={{ fontSize: "0.85rem" }} />
          </IconButton>
        )}
      </Box>
      <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 600, mt: "2px" }}>
        {label}
      </Typography>

      {/* Action sheet */}
      <Dialog open={actionOpen} onClose={() => setActionOpen(false)} sx={dialogSx}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: "8px" }}>
          {label}
          <IconButton size="small" onClick={() => setActionOpen(false)} sx={{ color: "rgba(255,255,255,0.4)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: "8px" }}>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.6)", mb: "14px", lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              component="button"
              onClick={handleRetake}
              sx={{
                width: "100%",
                padding: "13px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "10px",
                fontSize: "0.88rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                "&:hover": { background: "rgba(253,185,64,0.06)", borderColor: "rgba(253,185,64,0.3)" },
              }}
            >
              <ReplayIcon sx={{ fontSize: "1rem" }} />
              Retake photo
            </Box>
            <Box
              component="button"
              onClick={handleRemove}
              sx={{
                width: "100%",
                padding: "13px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#F87171",
                borderRadius: "10px",
                fontSize: "0.88rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                "&:hover": { background: "rgba(239,68,68,0.06)", borderColor: "rgba(239,68,68,0.3)" },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
              Remove photo
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function PhotosMode({ frontPhoto, setFrontPhoto, backPhoto, setBackPhoto, subDbPhoto, setSubDbPhoto, existingFront, existingBack, existingSubDb, onSubmit, isLoading }) {
  const [noPhotoError, setNoPhotoError] = useState(false);
  const [showSubDb, setShowSubDb] = useState(!!existingSubDb);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [activeFront, setActiveFront] = useState(existingFront);
  const [activeBack, setActiveBack] = useState(existingBack);
  const [activeSubDb, setActiveSubDb] = useState(existingSubDb);

  const isEditMode = !!(existingFront || existingBack || existingSubDb);
  const hasFront = !!frontPhoto || !!activeFront;
  const hasBack = !!backPhoto || !!activeBack;
  const allRequired = hasFront && hasBack;
  const anyNewPhoto = !!frontPhoto || !!backPhoto || !!subDbPhoto;

  // Auto-clear the required-photos error once both required slots are filled
  useEffect(() => {
    if (allRequired) setNoPhotoError(false);
  }, [allRequired]);

  const handleClearAll = () => {
    setFrontPhoto(null);
    setBackPhoto(null);
    setSubDbPhoto(null);
    setActiveFront(null);
    setActiveBack(null);
    setActiveSubDb(null);
    setShowSubDb(false);
    setNoPhotoError(true);
    setClearAllOpen(false);
  };

  const handleConfirm = () => {
    if (!allRequired) {
      setNoPhotoError(true);
      return;
    }
    onSubmit();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
      <Box sx={{ display: "flex", gap: "10px", width: "100%" }}>
        <PhotoSlot
          label="Front"
          required
          inputId="coc-front-input"
          photo={frontPhoto}
          setPhoto={setFrontPhoto}
          existingUrl={activeFront}
          onRemoveCallback={() => { setActiveFront(null); setNoPhotoError(true); }}
        />
        <PhotoSlot
          label="Back"
          required
          inputId="coc-back-input"
          photo={backPhoto}
          setPhoto={setBackPhoto}
          existingUrl={activeBack}
          onRemoveCallback={() => { setActiveBack(null); setNoPhotoError(true); }}
        />
        {showSubDb && (
          <PhotoSlot
            label="Sub-DB test"
            required={false}
            inputId="coc-subdb-input"
            photo={subDbPhoto}
            setPhoto={setSubDbPhoto}
            existingUrl={activeSubDb}
            onRemoveCallback={() => { setActiveSubDb(null); setShowSubDb(false); }}
          />
        )}
      </Box>

      {!showSubDb && (
        <Box
          component="button"
          onClick={() => setShowSubDb(true)}
          sx={{
            alignSelf: "flex-start",
            background: "transparent",
            border: "1px dashed rgba(253,185,64,0.35)",
            color: AMBER,
            padding: "10px 16px",
            borderRadius: "8px",
            fontSize: "0.82rem",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            "&:hover": { background: "rgba(253,185,64,0.06)" },
          }}
        >
          + Add photo (sub-DB test report)
        </Box>
      )}

      {!isEditMode && anyNewPhoto && (
        <Box
          component="button"
          onClick={() => setClearAllOpen(true)}
          sx={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.4)",
            fontSize: "0.76rem",
            fontFamily: "inherit",
            cursor: "pointer",
            padding: "8px 0 0",
            textDecoration: "underline",
            "&:hover": { color: "#F87171" },
          }}
        >
          Clear all photos
        </Box>
      )}

      {/* Clear all confirm dialog */}
      <Dialog open={clearAllOpen} onClose={() => setClearAllOpen(false)} sx={dialogSx}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Clear all COC photos?
          <IconButton size="small" onClick={() => setClearAllOpen(false)} sx={{ color: "rgba(255,255,255,0.4)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.55 }}>
            This removes every captured Front/Back/Sub-DB test photo. You'll need to retake them before you can confirm this COC.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: "16px", pb: "16px", gap: "8px" }}>
          <Button
            onClick={() => setClearAllOpen(false)}
            sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearAll}
            sx={{ color: "#F87171", fontWeight: 700, textTransform: "none" }}
          >
            Yes — clear all
          </Button>
        </DialogActions>
      </Dialog>

      <Collapse in={noPhotoError} sx={{ width: "100%" }}>
        <Box sx={{ backgroundColor: "rgba(255,80,80,0.08)", border: "1px solid rgba(255,80,80,0.25)", borderRadius: "8px", px: "14px", py: "10px" }}>
          <Typography sx={{ color: "#ff5050", fontSize: "0.82rem", lineHeight: 1.5 }}>
            Front and Back photos are required.
          </Typography>
        </Box>
      </Collapse>

      <Collapse in={allRequired} sx={{ width: "100%" }}>
        <Box sx={{ backgroundColor: "rgba(253,185,64,0.08)", border: "1px solid rgba(253,185,64,0.25)", borderRadius: "8px", px: "14px", py: "10px" }}>
          <Typography sx={{ color: AMBER, fontSize: "0.82rem", lineHeight: 1.5 }}>
            You can retake or remove any photo until you confirm. Once confirmed, the COC is locked.
          </Typography>
        </Box>
      </Collapse>

      {isLoading ? (
        <CircularProgress sx={{ color: AMBER, mt: "8px" }} />
      ) : (
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!allRequired}
          sx={{
            width: "100%",
            backgroundColor: AMBER,
            color: "#0d1117",
            fontWeight: 800,
            fontSize: "0.95rem",
            letterSpacing: "0.06em",
            borderRadius: "10px",
            py: "12px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "rgba(253,185,64,0.3)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          Confirm
        </Button>
      )}
    </Box>
  );
}

function PdfMode({ pdfFile, setPdfFile, existingPdf, onSubmit, isLoading }) {
  const [noFileError, setNoFileError] = useState(false);

  const hasFile = !!pdfFile || !!existingPdf;
  const displayName = pdfFile
    ? pdfFile.name
    : existingPdf
    ? existingPdf.split("/").pop()
    : "Tap to upload a signed COC PDF";

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfFile(file);
    setNoFileError(false);
  };

  const handleConfirm = () => {
    if (!hasFile) {
      setNoFileError(true);
      return;
    }
    onSubmit();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
      <input
        accept="application/pdf"
        id="coc-pdf-input"
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <label htmlFor="coc-pdf-input" style={{ width: "100%", cursor: "pointer" }}>
        <Box
          sx={{
            width: "100%",
            aspectRatio: "1.6",
            backgroundColor: CARD_BG,
            border: `1px dashed ${hasFile ? AMBER : "rgba(255,255,255,0.15)"}`,
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <UploadFileOutlinedIcon sx={{ color: AMBER, opacity: hasFile ? 1 : 0.55, fontSize: "2.5rem" }} />
          <Typography sx={{ color: hasFile ? "#fff" : "rgba(255,255,255,0.4)", fontSize: "0.85rem", textAlign: "center", px: "16px" }}>
            {displayName}
          </Typography>
        </Box>
      </label>

      <Collapse in={noFileError} sx={{ width: "100%" }}>
        <Box sx={{ backgroundColor: "rgba(255,80,80,0.08)", border: "1px solid rgba(255,80,80,0.25)", borderRadius: "8px", px: "14px", py: "10px" }}>
          <Typography sx={{ color: "#ff5050", fontSize: "0.82rem", lineHeight: 1.5 }}>
            Please select a PDF file first.
          </Typography>
        </Box>
      </Collapse>

      {isLoading ? (
        <CircularProgress sx={{ color: AMBER, mt: "8px" }} />
      ) : (
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!hasFile}
          sx={{
            width: "100%",
            backgroundColor: AMBER,
            color: "#0d1117",
            fontWeight: 800,
            fontSize: "0.95rem",
            letterSpacing: "0.06em",
            borderRadius: "10px",
            py: "12px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "rgba(253,185,64,0.3)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          Confirm
        </Button>
      )}
    </Box>
  );
}

function ApproveDialog({ open, onClose, id, job_card_reference_id }) {
  const navigate = useNavigate();
  const [downloadCOC] = useDownloadDigitalCOCMutation();

  const handleDownload = () => {
    downloadCOC({ job_card_id: id })
      .unwrap()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `job_card_${id}_digital_coc.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert("Failed to download file. Please try again."));
  };

  return (
    <Dialog open={open} onClose={onClose} sx={dialogSx}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Install Approved
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.4)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.6 }}>
          <Box component="span" sx={{ color: AMBER, wordBreak: "break-all" }}>
            {job_card_reference_id}
          </Box>
          {" "}has been approved and digital COC has been attached.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: "16px", pb: "16px" }}>
        <Button
          startIcon={<ArrowDownwardIcon />}
          onClick={handleDownload}
          sx={{ color: "rgba(255,255,255,0.6)", textTransform: "uppercase", fontSize: "0.8rem", fontWeight: 600 }}
        >
          Download COC
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate("/QE/")}
          sx={{
            backgroundColor: AMBER,
            color: "#0d1117",
            fontWeight: 700,
            borderRadius: "8px",
            px: "20px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
          }}
        >
          Home
        </Button>
      </DialogActions>
    </Dialog>
  );
}
